import React, { useState, useEffect, useContext, useRef } from "react";
import Modal from "../components/Modal";
import { AuthContext } from "../context/AuthContext";
import axiosInstance from "../utils/axiosInstance";
import { formatStatus, getStatusBadgeClass } from "../utils/Utils";
import Notification from "../components/Notification";
import {
  Button,
  EmptyState,
  PageLoader,
  SectionHeading,
  Spinner,
  Textarea,
  buttonClass,
} from "../components/ui";

const SupportChatBox = ({ ticketId, role }) => {
  const [newMessage, setNewMessage] = useState("");
  const [ticket, setTicket] = useState();
  const [messages, setMessages] = useState([]);
  const [attachment, setAttachment] = useState(null);
  const [canSend, setCanSend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sendMessageLoading, setSendMessageLoading] = useState(false);
  const [closeTicketLoading, setCloseTicketLoading] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [newMessageNotification, setNewMessageNotification] = useState(false);
  const { user } = useContext(AuthContext);
  const messagesEndRef = useRef(null);
  const prevMessagesRef = useRef([]);

  useEffect(() => {
    const fetchMessages = async (showLoader = true) => {
      if (showLoader) setLoading(true);
      try {
        const response = await axiosInstance.get(`/tickets/${ticketId}`);
        setTicket(response.data);
        setMessages(response.data.ticket_messages);
        prevMessagesRef.current = response.data.ticket_messages;
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [ticketId]);

  useEffect(() => {
    if (ticket?.status !== "CLOSED") {
      const fetchMessages = async (showLoader = true) => {
        if (showLoader) setLoading(true);
        try {
          const response = await axiosInstance.get(`/tickets/${ticketId}`);
          setMessages(response.data.ticket_messages);
        } finally {
          setLoading(false);
        }
      };
      const pollingInterval = setInterval(() => fetchMessages(false), 5000);
      return () => clearInterval(pollingInterval);
    }
  }, [ticket]);

  useEffect(() => {
    if (
      messages.length > 0 &&
      messages.length > prevMessagesRef.current.length
    ) {
      const lastMessage = messages[messages.length - 1];

      if (lastMessage.user_id !== user.id) {
        setNewMessageNotification(true);
        setTimeout(() => {
          scrollToBottom();
        }, 2000);
      }
    }
    prevMessagesRef.current = messages;
    handleCanSend(messages);
  }, [messages]);

  const handleCanSend = (allMessages) => {
    const sortedMessages = [...allMessages].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );

    if (sortedMessages.length > 0) {
      const lastMessage = sortedMessages[0];

      if (ticket.status === "CLOSED") {
        setCanSend(false);
        return;
      }

      // if (lastMessage.user_id !== user.id) {
      //   setCanSend(true);
      // } else {
      //   if (sortedMessages.length > 2) {
      //     const clientMessageCount = sortedMessages
      //       .slice(0, 3)
      //       .filter((message) => message.user_id === user.id).length;

      //     if (clientMessageCount < 3) {
      //       setCanSend(true);
      //       return;
      //     } else {
      //       setCanSend(false);
      //     }
      //   }
      // }
    } else {
      setCanSend(true);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async () => {
    if (newMessage.trim() !== "") {
      setSendMessageLoading(true);
      const messageData = {
        ticket_id: ticketId,
        user_id: user.id,
        message: newMessage,
        file_name: attachment ? attachment.name : "",
        created_at: new Date().toLocaleString(),
      };

      const formData = new FormData();
      formData.append("jsonData", JSON.stringify(messageData));

      if (attachment) {
        formData.append("file", attachment);
      }

      try {
        const response = await axiosInstance.post(
          `/tickets/${ticketId}/send-message`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const newMessages = [...messages, response.data];

        setMessages(newMessages);

        setNewMessage("");
        setAttachment(null);
        handleCanSend(newMessages);
        setSendMessageLoading(false);
      } catch (error) {
        setSendMessageLoading(false);
        console.error("Error sending message:", error);
      }
    }
  };

  const handleAttachment = (e) => {
    setAttachment(e.target.files[0]);
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handleCloseTicket = async () => {
    try {
      setCloseTicketLoading(true);
      await axiosInstance.put(`/tickets/${ticket.id}/close`);
    } finally {
      setShowCloseModal(false);
      setTicket({ ...ticket, status: "CLOSED" });
      setCloseTicketLoading(false);
    }
  };

  const handleReopenTicket = async () => {
    try {
      setCloseTicketLoading(true);
      await axiosInstance.put(`/tickets/${ticket.id}/reopen`);
    } finally {
      setShowReopenModal(false);
      setTicket({ ...ticket, status: "OPEN" });
      setCloseTicketLoading(false);
      setCanSend(true);
    }
  };

  if (loading) {
    return <PageLoader label="Loading the conversation…" />;
  }

  const isMessageFromMe = (message) => message.user_id === user.id;

  return (
    <div className="flex h-full flex-col">
      <div className="flex w-full flex-1 flex-col">
        <SectionHeading
          title="Support Chat"
          badge={
            ticket?.status ? (
              <span className={getStatusBadgeClass(ticket.status)}>
                {formatStatus(ticket.status)}
              </span>
            ) : null
          }
        >
          {ticket?.status !== "CLOSED" ? (
            <Button variant="ghost" size="sm" onClick={() => setShowCloseModal(true)}>
              Close Ticket
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => setShowReopenModal(true)}>
              Reopen Ticket
            </Button>
          )}
        </SectionHeading>

        <div className="mt-4 h-64 space-y-3 overflow-y-auto rounded-[14px] border border-gray-200 bg-gray-50 p-3 dark:border-[#2c2c2e] dark:bg-[#161617]">
          {messages && messages.length > 0 ? (
            messages.map((msg, index) => (
              <div
                key={index}
                className={`flex flex-col ${
                  isMessageFromMe(msg) ? "items-end" : "items-start"
                }`}
              >
                <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400 dark:text-slate-500">
                  {isMessageFromMe(msg)
                    ? "Me"
                    : role.toUpperCase() === "ADMIN"
                    ? "Client"
                    : "Support Agent"}{" "}
                </div>
                <div
                  className={`w-fit max-w-xs break-words rounded-[14px] px-3 py-2 text-sm ${
                    isMessageFromMe(msg)
                      ? "rounded-br-sm bg-white text-gray-800 shadow-sm ring-1 ring-gray-200 dark:bg-[#262628] dark:text-slate-100 dark:ring-[#3a3a3d]"
                      : "rounded-bl-sm bg-gradient-to-r from-red-600 to-orange-500 text-white"
                  }`}
                >
                  {msg.message}
                  {msg.file_name && (
                    <div
                      className={`mt-1.5 inline-flex max-w-full items-center gap-1.5 truncate rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        isMessageFromMe(msg)
                          ? "bg-gray-100 text-gray-600 dark:bg-[#303032] dark:text-slate-300"
                          : "bg-white/20 text-white"
                      }`}
                      title={msg.file_name}
                    >
                      📎 {msg.file_name}
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <EmptyState title="No messages yet" hint="Write the first message below." />
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="mt-3 flex items-end gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            rows="2"
            placeholder="Type your message here…"
            disabled={!canSend}
            className="flex-1 disabled:opacity-60"
          />
          <label
            htmlFor="fileInput"
            title="Attach a file"
            className="inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 transition hover:border-red-400/60 hover:text-red-500 dark:border-[#3a3a3d] dark:bg-[#262628] dark:text-slate-400"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.8"
              stroke="currentColor"
              className="h-5 w-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13"
              />
            </svg>
          </label>
          <input
            id="fileInput"
            type="file"
            onChange={handleAttachment}
            className="hidden"
            disabled={!canSend}
          />
        </div>

        {attachment ? (
          <p className="mt-2 truncate text-xs text-gray-500 dark:text-slate-400" title={attachment.name}>
            Attaching: <span className="font-semibold">{attachment.name}</span>
          </p>
        ) : null}

        <button
          onClick={sendMessage}
          className={buttonClass({ className: "mt-3 w-full" })}
          disabled={!canSend || sendMessageLoading}
        >
          {sendMessageLoading ? <Spinner /> : null}
          {sendMessageLoading ? "Sending…" : "Send Message"}
        </button>
      </div>

      {showCloseModal && (
        <Modal
          title="Close Ticket"
          content={`Are you sure you want to close the ticket?`}
          onConfirm={handleCloseTicket}
          onCancel={() => setShowCloseModal(false)}
          action={closeTicketLoading ? "Closing..." : "Yes"}
          disabled={closeTicketLoading}
        />
      )}

      {showReopenModal && (
        <Modal
          title="Reopen Ticket"
          content={`Are you sure you want to reopen the ticket?`}
          onConfirm={handleReopenTicket}
          onCancel={() => setShowReopenModal(false)}
          action={closeTicketLoading ? "Opening..." : "Yes"}
          disabled={closeTicketLoading}
        />
      )}

      {newMessageNotification && (
        <Notification
          setShowNotification={setNewMessageNotification}
          message={"You have new message from Support"}
          isError={false}
        />
      )}
    </div>
  );
};

export default SupportChatBox;
